import type { ContextValue } from '@datadog/browser-core';

enum Severity {
  Trace,
  Debug,
  Information,
  Warning,
  Error,
  Critical,
  None
}

type LogMessage = {
  /**
   * An optional exception message for this log entry
   */
  exception?: string;
  /**
   * An optional {@link Error}
   */
  error?: Error;
  /**
   * The log message to record
   */
  message: string;
  /**
   * The severity of the error message
   * Severities above {@link Severity.Error} are automatically recorded in Datadog via datadogRum.addError
   */
  severity: Severity;
};

type DatadogInterface = {
  /**
   * Updates the current authenticated user and changes the session to an authenticated session
   * @param userId the userId of the user
   */
  setAuthenticatedUser(userId: string): void;
  /**
   * Clears the authenticated user changing the session back to an anonymous session
   */
  clearAuthenticatedUser(): void;
  /**
   * Creates a new {@link DatadogSpan} for the given component and name
   * @param component the component starting the span
   * @param name the name of the span
   */
  startSpan(component: string, name: string): DatadogSpan;
  /**
   * Logs a message to Datadog
   * @param logMessage the message details to log to Datadog
   */
  log(logMessage: LogMessage): void;
  /**
   * Logs an error to Datadog. The provided err is directly provided to Datadog
   *
   * @param err the error details
   * @param message the message to accompany the error
   */
  logError(err: unknown, message?: string): void;
  /**
   * Records an exception into Datadog
   * @param exception the exception, should generally be an {@link Error}
   * @param properties any additional properties to be recorded with the exception
   */
  recordException(exception: unknown, properties: object): void;
  /**
   * Records a Datadog action in the current session/view
   * @param actionName the name of the action to record
   * @param context any additional context related to the action
   */
  addAction(actionName: string, context?: object): void;
  /**
   * Appends the version of the provided module to the Datadog context when sending events
   * @param moduleName the module that is updating the version
   * @param version the version information to append
   */
  addVersion(moduleName: string, version: string): void;
    /**
   * Adds the provided key/value pair to the global context when value is an object
   * @param key the key that associate with the value
   * @param value an object or an string to be added to the global context
   */
  addToRumGlobalContext(key: string, value: object | string): void;
  /**
   * Gets the value associated with the provided key from the global context
   * @param key the key to get the value for
   */
  getRumGlobalContext(key: string): ContextValue;
};

export interface DatadogSpan {
  /**
   * Adds an event to this span. It uses the originally provided component and span name
   * @param name the name of the event being added
   * @param attributes any additional attributes to be included in the event
   */
  addEvent(name: string, attributes?: unknown): DatadogSpan;

  /**
   * Adds an error event to this span
   * @param name the name of the error
   * @param attributes any additional attributes with details about the error
   */
  addErrorEvent(name: string, attributes?: unknown): DatadogSpan;

  /**
   * Adds an error event to the span, but with a specified severity
   * @param name the name of the error event
   * @param attributes any additional attributes with details about the error
   * @param severity the severity of the event
   */
  addErrorEvent(name: string, attributes?: unknown, severity?: Severity): DatadogSpan;

  /**
   * Ends the span recording an ending event with the timing
   * @param attributes any additional attributes to add to the ending action
   */
  end(attributes?: object): void;
}

/**
 * An empty span to be used if Datadog isn't injected correctly
 */
const emptyDatadogSpan: DatadogSpan = {
  addErrorEvent(_name: string, _attributes?: unknown): DatadogSpan {
    return this;
  },
  addEvent(_name: string, _attributes?: unknown): DatadogSpan {
    return this;
  },
  end() {}
};

export { emptyDatadogSpan, Severity };
export type { DatadogInterface, LogMessage };
